import { clsx } from 'clsx';
import moment from 'moment/moment';
import {
  EscalatedStatusIcon,
  InProgressStatusIcon,
  ResolvedStatusIcon,
} from 'src/pages/ConversationsPage/ConversationList/ConversationStatusIcons';
import { forwardRef } from 'react';
import { MarkdownSubjectRenderer } from 'src/components/MarkdownRenderer/MarkdownSubjectRenderer';

// The conversation fields a list item shows.
export interface ConversationSummary {
  id?: string | number;
  _id?: string;
  selected?: boolean;
  created_at?: string;
  subject?: string;
  status?: string;
  // Status label added when the chats are loaded.
  friendlyStatus?: string;
  ticket_id?: string | number;
  [key: string]: unknown;
}

export const ConversationListItem = forwardRef<
  HTMLLIElement,
  { chat?: ConversationSummary; onClick?: (chat: ConversationSummary) => void }
>(({ chat, onClick }, ref) => {
  return (
    <li ref={ref}>
      <button
        type="button"
        className={clsx(
          'py-6 px-2.5 cursor-pointer flex flex-row items-center border-collapse border border-transparent border-b-light rounded-sm-5 hover:border-purple_opacity_30 max-h-[106px] justify-between gap-5',
          chat.selected && 'bg-[#FAF9FF] !border-purple_opacity_30',
          'w-full text-left',
        )}
        onClick={() => onClick(chat)}
      >
        <div className={'min-w-[75px] max-w-[75px] max-lg:hidden'}>
          <p
            className={clsx(
              'text-xs font-medium text-ebony_opacity_50',
              chat.selected && 'text-ebony_opacity_80',
            )}
          >
            {moment(chat.created_at).format('lll')}
          </p>
        </div>
        <div className={'flex-1 overflow-hidden'}>
          <p
            className={clsx(
              'text-sm text-ebony_opacity_80 font-semibold overflow-ellipsis text-nowrap overflow-hidden max-h-6',
              chat.selected && 'text-ebony font-semibold',
            )}
          >
            <MarkdownSubjectRenderer>{chat.subject}</MarkdownSubjectRenderer>
          </p>
          <p
            className={clsx(
              'mt-0.5 text-xs font-medium text-ebony_opacity_50 whitespace-break-spaces',
              chat.selected && 'text-ebony_opacity_80',
            )}
          >
            ID: {chat._id}
            {chat.ticket_id ? `  |  Ticket ID: ${chat.ticket_id}` : ''}
          </p>
        </div>
        <div
          className={clsx(
            'text-xs font-medium text-ebony_opacity_50 flex flex-row items-center gap-[5px] max-lg:hidden',
            chat.selected && 'text-ebony_opacity_80',
          )}
        >
          <p
            className={clsx(
              chat.status === 'open' && 'text-[#009CB2]',
              ['closed', 'auto_closed'].includes(chat.status) && 'text-[#389D61]',
              chat.status === 'escalated' && 'text-[#C11A45]',
            )}
          >
            {chat.friendlyStatus}
          </p>
          {chat.status === 'open' && (
            <InProgressStatusIcon fill={'#009CB2'} selected={chat.selected} />
          )}
          {['closed', 'auto_closed'].includes(chat.status) && (
            <ResolvedStatusIcon fill={'#389D61'} selected={chat.selected} />
          )}
          {chat.status === 'escalated' && (
            <EscalatedStatusIcon fill={'#C11A45'} selected={chat.selected} />
          )}
        </div>
      </button>
    </li>
  );
});
